import { IconMoon, IconSun } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { Button } from './ui/Button';

// Flips `data-theme` on <html> between dark/light and persists the choice.
// The default theme + first-paint restore live in index.html (a 2-line script
// reads `dz-theme`, falling back to `prefers-color-scheme` when nothing's
// stored, before paint so there's no flash). This is just the switch,
// rendered as the topbar's ghost icon button.

type Theme = 'dark' | 'light';

// index.html's inline script always sets the attribute correctly (stored
// value, or prefers-color-scheme, or dark) before this component ever mounts,
// so trusting the attribute here is sufficient — no second matchMedia call
// needed.
function current(): Theme {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

export function ThemeToggle(): JSX.Element {
  const [theme, setTheme] = createSignal<Theme>(current());

  const toggle = (): void => {
    const next: Theme = theme() === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    // Keep the browser/PWA chrome color in sync with --background per theme.
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', next === 'light' ? '#f7f8fa' : '#050608');
    try {
      localStorage.setItem('dz-theme', next);
    } catch {
      // ponytail: private-mode storage throws; the toggle still works this session.
    }
    setTheme(next);
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={t('theme.toggle')}
      aria-pressed={theme() === 'dark'}
    >
      {/* Show the icon of the theme you'll switch TO: sun while dark, moon while light. */}
      <Show when={theme() === 'dark'} fallback={<IconMoon size="1rem" />}>
        <IconSun size="1rem" />
      </Show>
    </Button>
  );
}
